import type { VfsName } from './db/vfs.js';

/**
 * Runtime configuration of the web app (docs/07 §11); values come from Vite's `VITE_*`.
 * `VITE_API_URL=` (empty) means «same origin»: the dev server proxies `/api`, `/v1` and `/sync`
 * (vite.config.ts), which is how a real iPhone reaches the local stack over HTTPS.
 */
const rawApiUrl = import.meta.env.VITE_API_URL;
export const API_URL: string =
  rawApiUrl === undefined ? 'http://localhost:3000' : rawApiUrl.replace(/\/$/, '');

/** PowerSync endpoint forced by the client (absolute, or a path on this origin); undefined = the API's. */
export const POWERSYNC_URL: string | undefined = resolveUrl(import.meta.env.VITE_POWERSYNC_URL);

function resolveUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (value.startsWith('/') && typeof location !== 'undefined') return `${location.origin}${value}`;
  return value;
}

/**
 * Development override read from localStorage (`flow.vfs`, `flow.multiTabs`), so the persistence
 * matrix can switch VFS and multi-tab mode from the browser console without rebuilding.
 */
function override(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export const PREFERRED_VFS: VfsName =
  (override('flow.vfs') as VfsName | null) ??
  (import.meta.env.VITE_SQLITE_VFS as VfsName | undefined) ??
  'OPFSCoopSyncVFS';

function flag(key: string, env: string | undefined): boolean | undefined {
  const value = override(key) ?? env;
  return value === 'true' ? true : value === 'false' ? false : undefined;
}

/** `true`/`false` to force the SDK's multi-tab mode; undefined keeps the SDK default. */
export const MULTI_TABS: boolean | undefined = flag(
  'flow.multiTabs',
  import.meta.env.VITE_MULTI_TABS,
);

/** `false` runs SQLite in the page instead of a worker (matrix experiments only). */
export const WEB_WORKER: boolean | undefined = flag('flow.webWorker', undefined);

/**
 * `true` makes the SDK record every statement as a `[SQL] …` performance measure, including the time
 * it waited for the connection (write-latency diagnostics, f1-local-write-latency).
 */
export const DEBUG_SQL: boolean = flag('flow.debugSql', undefined) === true;

/** `false` opens the database without the local full-text index (perf diagnostics only). */
export const SEARCH_INDEX: boolean = flag('flow.search', undefined) !== false;

export const APP_VERSION: string = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev';
